import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import TradeCard from '../portfolio/TradeCard';
import { MemoizedTradeItem } from './TradeItem';

const TradesList = ({ tabValue, searchText, sortType, priceChangeConfig }) => {
  // Only select the data we need for this component
  const tradeHistory = useSelector((state) => state.trade.tradeHistory);
  const quotesData = useSelector((state) => state.trade.quotesData);

  const getSortedAndFilteredTrades = (trades) => {
    // First filter by search text
    const filtered = trades.filter((trade) =>
      trade.script.name.toLowerCase().includes(searchText.toLowerCase())
    );

    // Then sort according to selected sort type
    switch (sortType) {
      case 'atoz':
        return [...filtered].sort((a, b) => a.script.name.localeCompare(b.script.name));
      case 'lastUpdated':
        return [...filtered].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      default:
        return filtered;
    }
  };

  // Use useMemo to cache the filtered and sorted trade history
  const filteredTradeHistory = useMemo(() => {
    switch (tabValue) {
      case 0: // ACTIVE
        return getSortedAndFilteredTrades(
          tradeHistory
            .filter((trade) => trade.type === 'OPEN')
            .map((trade) => ({
              ...trade,
              script: { ...trade.script, ...quotesData[trade?.script?.instrument_token] },
            }))
        );

      case 1: // LIMITS
        return getSortedAndFilteredTrades(
          tradeHistory
            .filter((trade) => ['PENDING'].includes(trade.type))
            .sort((a, b) => {
              const priority = { PENDING: 1, EXECUTED: 2, CANCELLED: 3 };
              return priority[a.type] - priority[b.type];
            })
        );

      default:
        return getSortedAndFilteredTrades(tradeHistory);
    }
  }, [tradeHistory, quotesData, tabValue, searchText, sortType]);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pb: 2 }}>
      {filteredTradeHistory.length > 0 ? (
        filteredTradeHistory.map((trade) => {
          if ((tabValue === 0 && trade.type === 'OPEN') || (tabValue === 1 && trade.type === 'PENDING')) {
            return (
              <MemoizedTradeItem
                key={trade._id}
                trade={trade}
                priceChangeConfig={priceChangeConfig}
                closeFromTrade
              />
            );
          }
          return null;
        })
      ) : (
        <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center', padding: 4 }}>
          {tabValue === 0 ? 'NO ACTIVE TRADES' : 'NO ACTIVE LIMITS'}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(TradesList);
